import type {ChatMessage,CodexNotification,AcceptedConversationAttachment,ResponseAnnotation} from '@reados/contracts';

/** Local feedback while the API/native thread accepts a submission. These
 * values are never persisted or represented as upstream protocol events. */
export interface PendingSubmission {
  id:string;
  text:string;
  attachments?:AcceptedConversationAttachment[];
  responseAnnotations?:ResponseAnnotation[];
  threadId?:string;
  /** Only the POST response can bind this optimistic submission to a native turn. */
  nativeTurnId?:string;
  startedAtMilliseconds:number;
  existingMessageIds:ReadonlySet<string>;
}

export type SubmissionResult=boolean|{accepted:true;turnId?:string;composerTransferred?:boolean};
export interface SubmissionPresentation {id:string;responseAnnotations?:ResponseAnnotation[];threadId?:string;existingMessageIds?:ReadonlySet<string>}

function presentation(id:string,responseAnnotations?:ResponseAnnotation[]){return {readexWebPresentationID:`pending-user:${id}`,readexWebTurnPresentationID:`pending-turn:${id}`,...(responseAnnotations?.length?{submissionId:id,readexWebResponseAnnotations:responseAnnotations}:{})};}

function isCandidate(message:ChatMessage,pending:PendingSubmission):boolean {
  return message.role==='user'&&!pending.existingMessageIds.has(message.id)
    &&message.submissionId===pending.id&&(!pending.threadId||message.threadId===pending.threadId);
}

export function submittedUserMessage(messages:ChatMessage[],pending:PendingSubmission):ChatMessage|undefined {
  if(!pending.nativeTurnId)return undefined;
  return acceptedPresentationMessage(messages,pending.nativeTurnId,pending);
}

function acceptedPresentationMessage(messages:ChatMessage[],turnId:string,submission:SubmissionPresentation):ChatMessage|undefined {
  const candidates=messages.filter(message=>message.role==='user'&&message.turnId===turnId
    &&(!submission.threadId||message.threadId===submission.threadId)&&!submission.existingMessageIds?.has(message.id));
  const exact=candidates.filter(message=>message.submissionId===submission.id);
  if(exact.length)return exact.length===1?exact[0]:undefined;
  const unclaimed=candidates.filter(message=>!message.submissionId);
  return unclaimed.length===1?unclaimed[0]:undefined;
}

export function withPendingSubmission(messages:ChatMessage[],pending:PendingSubmission|null,threadId?:string,submissionIds:Readonly<Record<string,string|SubmissionPresentation>>={}):ChatMessage[] {
  const turnUsers=new Map<string,ChatMessage[]>();
  for(const message of messages)if(message.role==='user'&&typeof message.turnId==='string'){
    const users=turnUsers.get(message.turnId)??[];users.push(message);turnUsers.set(message.turnId,users);
  }
  const presentationOwners=new Map<string,ChatMessage|undefined>();
  const presented=messages.map(message=>{
    const id=message.role==='user'&&typeof message.turnId==='string'?submissionIds[message.turnId]:undefined;
    if(!id)return message;
    if(typeof id==='string')return {...message,...presentation(id)};
    const turnId=message.turnId as string;
    if(!presentationOwners.has(turnId))presentationOwners.set(turnId,acceptedPresentationMessage(turnUsers.get(turnId)??[],turnId,id));
    if(presentationOwners.get(turnId)!==message)return message;
    return {...message,...presentation(id.id,id.responseAnnotations)};
  });
  if(!pending)return presented;
  const authoritative=submittedUserMessage(messages,pending);
  if(authoritative){const ownerIndex=messages.indexOf(authoritative);return presented.map((message,index)=>index===ownerIndex?{...message,...presentation(pending.id,pending.responseAnnotations)}:message);}
  // SSE can beat the HTTP response. Use the accepted submission identity to avoid a duplicate user bubble,
  // but do not acknowledge or clear local state until POST supplies the turn id.
  const candidates=!pending.nativeTurnId?messages.filter(message=>isCandidate(message,pending)):[];
  const provisional=candidates.length===1?candidates[0]:undefined;
  if(provisional){const ownerIndex=messages.indexOf(provisional);return presented.map((message,index)=>index===ownerIndex?{...message,...presentation(pending.id,pending.responseAnnotations)}:message);}
  return [...presented,{id:`pending-user:${pending.id}`,role:'user',text:pending.text,status:'complete',attachments:pending.attachments,submissionId:pending.id,
    ...presentation(pending.id,pending.responseAnnotations),turnId:`pending-turn:${pending.id}`,threadId:pending.threadId??threadId,createdAt:new Date(pending.startedAtMilliseconds).toISOString()}];
}

export function pendingTurnStarted(messages:ChatMessage[],events:CodexNotification[],pending:PendingSubmission):boolean {
  const user=submittedUserMessage(messages,pending);
  if(!user||typeof user.turnId!=='string'||!user.turnId)return false;
  if(messages.some(message=>message.role!=='user'&&message.turnId===user.turnId&&message.threadId===user.threadId))return true;
  return events.some(event=>{
    if(event.method!=='turn/started')return false;
    const turn=event.params.turn;
    if(!turn||typeof turn!=='object')return false;
    const native=turn as Record<string,unknown>;
    // Bind to POST's turn id and its authoritative user item. Replayed history,
    // including identical text, cannot acknowledge a new submission.
    return native.id===user.turnId&&event.params.threadId===user.threadId;
  });
}
